import { Box, Typography } from "@mui/material";
import { Theme } from "@mui/material/styles";
import { BadgeSuccess } from "./BadgeSuccess";

export interface PreOfertaDato {
  label: string;
  value: string;
}

interface Props {
  /** Encabezado de la tarjeta, ej. "Tu pre-oferta · Imagenología". */
  eyebrow: string;
  /** Pastilla verde a la derecha, ej. "Pre-aprobado". */
  badge: string;
  /** Cifra principal ya formateada, ej. "$25,480". */
  amount: string;
  /** Unidad junto a la cifra, ej. "MXN + IVA / mes". */
  amountCaption: string;
  /** Línea bajo la cifra: periodo, condiciones y vigencia. */
  sub: string;
  /** Datos de la cuadrícula de dos columnas. */
  items: PreOfertaDato[];
  /**
   * `gradient` (default): degradado del color de marca con borde degradado, como la base del diseño
   * (Lendia, Flux). `flat`: fondo tenue plano sin el borde degradado (override de Mercader).
   */
  variant?: "gradient" | "flat";
}

/** Mezcla el color de marca con blanco (o transparente), como el `color-mix` del diseño. */
const mix = (color: string, percent: number, base = "#fff") => `color-mix(in srgb, ${color} ${percent}%, ${base})`;

const STAT_LABEL_SX = {
  fontSize: 11,
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  color: "tokens.textTertiary",
  mb: "3px",
} as const;

/**
 * Replica `.ld-offer` del diseño: tarjeta de una pre-oferta con eyebrow y badge, cifra grande, una línea de
 * condiciones y una cuadrícula de datos. La comparten las pre-ofertas por empresa (`ofertaPorEmpresa`);
 * cada página arma sus datos. No confundir con `OfertaCard`, la tarjeta de cada financiera del marketplace.
 */
export const CardPreOferta = ({ eyebrow, badge, amount, amountCaption, sub, items, variant = "gradient" }: Props) => (
  <Box
    sx={(theme: Theme) => {
      const brand = theme.palette.primary.main;
      return {
        position: "relative",
        borderRadius: "14px",
        p: "28px 30px",
        boxShadow: "0 4px 12px rgba(14,31,58,.08), 0 2px 4px rgba(14,31,58,.04)",
        overflow: "hidden",
        border: "1px solid",
        ...(variant === "flat"
          ? { bgcolor: mix(brand, 8), borderColor: mix(brand, 28) }
          : {
              background: `radial-gradient(120% 80% at 100% 0%, ${mix(brand, 12, "transparent")} 0%, transparent 55%), linear-gradient(180deg, ${mix(brand, 5)} 0%, #FBFCFE 100%)`,
              borderColor: mix(brand, 12),
              // Borde degradado del diseño (`.ld-offer::before`): un marco de 1.25px recortado con máscara.
              "&::before": {
                content: '""',
                position: "absolute",
                inset: 0,
                borderRadius: "inherit",
                p: "1.25px",
                background: `linear-gradient(118deg, ${mix(brand, 55, "transparent")} 0%, ${mix(brand, 28, "transparent")} 22%, ${mix(brand, 8, "transparent")} 48%, ${mix(brand, 0, "transparent")} 72%)`,
                WebkitMask: "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
                WebkitMaskComposite: "xor",
                maskComposite: "exclude",
                pointerEvents: "none",
              },
            }),
      };
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5, mb: "22px" }}>
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          // `--ld-blue-700` del diseño: el hover de la marca si lo define.
          color: (theme) => theme.palette.primary.hover ?? theme.palette.primary.main,
        }}
      >
        {eyebrow}
      </Typography>
      <Box sx={{ flexShrink: 0 }}>
        <BadgeSuccess>{badge}</BadgeSuccess>
      </Box>
    </Box>

    <Box sx={{ lineHeight: 1 }}>
      <Typography
        component="span"
        sx={{
          fontFamily: "Outfit",
          fontSize: 34,
          fontWeight: 600,
          letterSpacing: "-0.035em",
          fontVariantNumeric: "tabular-nums",
          color: "primary.dark",
          lineHeight: 1,
        }}
      >
        {amount}
      </Typography>
      <Typography component="span" sx={{ fontSize: 16, fontWeight: 400, color: "tokens.textTertiary", ml: 1 }}>
        {amountCaption}
      </Typography>
    </Box>

    <Typography sx={{ fontSize: 13, color: "tokens.textSecondary", mt: "2px" }}>{sub}</Typography>

    <Box sx={{ mt: "14px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px 24px" }}>
      {items.map((item) => (
        <Box key={item.label}>
          <Typography sx={STAT_LABEL_SX}>{item.label}</Typography>
          <Typography
            sx={{
              fontFamily: "Outfit",
              fontSize: 19,
              fontWeight: 600,
              color: "primary.dark",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {item.value}
          </Typography>
        </Box>
      ))}
    </Box>
  </Box>
);
